import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Loader2, Printer, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FIELD_SPORTS } from '@/lib/sportStats';
import { StatLegend } from '@/components/stats/StatLegend';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Print-optimized blank stat sheet for a single match (single-match mode)
 * or every match currently filtered in the schedule (bulk mode).
 *
 * Route shape:
 *   /events/:eventId/matches/:matchId/print-stat-sheet        (single)
 *   /events/:eventId/print-stat-sheets?ids=id1,id2,id3        (bulk)
 *
 * Uses browser print (Ctrl+P / Cmd+P). Each match renders on its own
 * printed page via `page-break-after: always`. When ?auto=1 is passed in
 * the URL the page fires `window.print()` as soon as data has loaded so
 * the "Print Sheet" row action feels one-click.
 */
function StatSheetBody({ data }) {
  const { event, match, sport_definition, team_a, team_b } = data;
  const stats = sport_definition?.stats || [];
  const isSetBased = sport_definition?.set_based;
  const isLandscape = sport_definition?.print_layout === 'landscape';

  // Ensure at least 2 walk-on blank rows per team so a walk-in player can
  // still be added on paper.
  const walkonRows = Array.from({ length: 2 });

  const dateLabel = match.date
    ? new Date(match.date + 'T00:00:00').toLocaleDateString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
      })
    : '—';
  const timeLabel = match.time || '—';
  const courtWord = FIELD_SPORTS.includes((event?.sport || '').toLowerCase()) ? 'Field' : 'Court';
  const courtLabel = match.venue
    ? `${match.venue}${match.court ? ` · ${courtWord} ${match.court}` : ''}`
    : (match.court ? `${courtWord} ${match.court}` : '—');
  const stageLabel = match.match_label || match.stage || '';

  const renderTeamTable = (team, side) => (
    <div className="stat-team-block">
      <div className="stat-team-header">
        {team.team_logo ? (
          <img
            src={`${API_URL}/api/files/${team.team_logo}`}
            alt=""
            className="stat-team-logo"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : null}
        <div>
          <div className="stat-team-side">Team {side.toUpperCase()}</div>
          <div className="stat-team-name">
            {team.name || team.placeholder || 'TBD'}
          </div>
        </div>
      </div>

      <table
        className={`stat-roster-table stats-cols-${stats.length}`}
        data-testid={`stat-sheet-${side}-table`}
      >
        <thead>
          <tr>
            <th className="col-jersey">#</th>
            <th className="col-name">Player</th>
            {stats.map((s) => (
              <th key={s.key} className="col-stat">
                {s.short || (s.label || s.key).toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {team.players.map((p, i) => (
            <tr key={`${side}-p-${i}`}>
              <td className="col-jersey">{p.jersey_number || ''}</td>
              <td className="col-name">{p.name}</td>
              {stats.map((s) => (
                <td key={s.key} className="col-stat blank"></td>
              ))}
            </tr>
          ))}
          {walkonRows.map((_, i) => (
            <tr key={`${side}-w-${i}`} className="walkon-row">
              <td className="col-jersey blank"></td>
              <td className="col-name blank"></td>
              {stats.map((s) => (
                <td key={s.key} className="col-stat blank"></td>
              ))}
            </tr>
          ))}
          <tr className="totals-row">
            <td colSpan={2} className="totals-label">TOTALS</td>
            {stats.map((s) => (
              <td key={s.key} className="col-stat blank"></td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <article className={`stat-sheet${isLandscape ? ' landscape' : ''}`} data-testid="stat-sheet">
      <header className="stat-header">
        <div className="stat-brand">
          {event.organization_logo ? (
            <img
              src={`${API_URL}/api/files/${event.organization_logo}`}
              alt=""
              className="stat-brand-logo"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : null}
          <div>
            {event.organization ? (
              <div className="stat-org-name">{event.organization}</div>
            ) : null}
            <h1 className="stat-event-name">{event.name}</h1>
          </div>
        </div>
        <div className="stat-meta">
          <div className="stat-meta-row">
            <span className="stat-meta-label">DATE</span>
            <span className="stat-meta-value">{dateLabel}</span>
          </div>
          <div className="stat-meta-row">
            <span className="stat-meta-label">TIME</span>
            <span className="stat-meta-value">{timeLabel}</span>
          </div>
          <div className="stat-meta-row">
            <span className="stat-meta-label">VENUE</span>
            <span className="stat-meta-value">{courtLabel}</span>
          </div>
          {stageLabel ? (
            <div className="stat-meta-row">
              <span className="stat-meta-label">STAGE</span>
              <span className="stat-meta-value">{stageLabel}</span>
            </div>
          ) : null}
          <div className="stat-meta-row">
            <span className="stat-meta-label">SPORT</span>
            <span className="stat-meta-value">{sport_definition.label}</span>
          </div>
        </div>
      </header>

      <section className="stat-scorebox">
        <div className="scorebox-team">
          <div className="scorebox-team-name">
            {team_a.name || team_a.placeholder || 'TBD'}
          </div>
          <div className="scorebox-final">
            <span className="scorebox-label">FINAL</span>
            <span className="scorebox-blank"></span>
          </div>
        </div>
        <div className="scorebox-vs">VS</div>
        <div className="scorebox-team">
          <div className="scorebox-team-name">
            {team_b.name || team_b.placeholder || 'TBD'}
          </div>
          <div className="scorebox-final">
            <span className="scorebox-label">FINAL</span>
            <span className="scorebox-blank"></span>
          </div>
        </div>
      </section>

      {isSetBased ? (
        <section className="stat-sets">
          <div className="stat-sets-label">SET SCORES</div>
          <table className="stat-sets-table">
            <thead>
              <tr>
                <th></th>
                <th>Set 1</th>
                <th>Set 2</th>
                <th>Set 3</th>
                <th>Set 4</th>
                <th>Set 5</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="team-cell">{team_a.name || 'Team A'}</td>
                <td className="blank"></td><td className="blank"></td>
                <td className="blank"></td><td className="blank"></td>
                <td className="blank"></td>
              </tr>
              <tr>
                <td className="team-cell">{team_b.name || 'Team B'}</td>
                <td className="blank"></td><td className="blank"></td>
                <td className="blank"></td><td className="blank"></td>
                <td className="blank"></td>
              </tr>
            </tbody>
          </table>
        </section>
      ) : null}

      <section className="stat-teams">
        {renderTeamTable(team_a, 'a')}
        {renderTeamTable(team_b, 'b')}
      </section>

      <StatLegend variant="print" sport={event.sport} codes={stats.map((s) => s.short || s.label)} testId="print-stat-legend" />

      <footer className="stat-footer">
        <div className="signature-line">
          <span>SCOREKEEPER SIGNATURE</span>
          <span className="signature-blank"></span>
        </div>
        <div className="signature-line">
          <span>REFEREE SIGNATURE</span>
          <span className="signature-blank"></span>
        </div>
        <div className="stat-footer-brand">scheduleguru.ws</div>
      </footer>
    </article>
  );
}

export default function PrintStatSheetPage() {
  const { eventId, matchId } = useParams();
  const [searchParams] = useSearchParams();
  const idsParam = searchParams.get('ids') || '';
  const autoPrint = searchParams.get('auto') === '1';

  const bulkIds = useMemo(() => {
    if (matchId) return [matchId];
    return idsParam.split(',').map((s) => s.trim()).filter(Boolean);
  }, [matchId, idsParam]);

  const [sheets, setSheets] = useState(null); // array of data payloads
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('session_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const results = await Promise.all(
          bulkIds.map(async (mid) => {
            const r = await fetch(
              `${API_URL}/api/events/${eventId}/matches/${mid}/blank-stat-sheet`,
              { headers }
            );
            if (!r.ok) {
              throw new Error(`HTTP ${r.status} for match ${mid}`);
            }
            return r.json();
          })
        );
        if (!cancelled) setSheets(results);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load stat sheet(s)');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (bulkIds.length) loadAll();
    else { setLoading(false); setError('No match id supplied'); }
    return () => { cancelled = true; };
  }, [eventId, bulkIds]);

  // Auto-fire the browser print dialog once sheets have hydrated so the
  // "Print Sheet" row action reads as one click. User can dismiss and
  // reprint via the button in the toolbar.
  useEffect(() => {
    if (autoPrint && sheets && !loading && !error) {
      // Give the DOM a beat to render (fonts, logos) before print.
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [autoPrint, sheets, loading, error]);

  if (loading) {
    return (
      <div className="print-loader">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="print-loader">
        <p className="text-slate-600">Could not load stat sheet: {error}</p>
        <Link to={`/schedule/${eventId}`} className="mt-4 text-[#00247D] underline">
          Back to schedule
        </Link>
      </div>
    );
  }

  return (
    <div className="print-stat-sheet-page">
      <div className="print-toolbar no-print">
        <Link
          to={`/schedule/${eventId}`}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#00247D]"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 hidden sm:inline">
            {sheets.length} sheet{sheets.length === 1 ? '' : 's'} ready
          </span>
          <Button
            onClick={() => window.print()}
            className="bg-[#00247D] hover:bg-[#001A5C]"
            data-testid="print-stat-sheet-btn"
          >
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        </div>
      </div>
      <div className="print-canvas">
        {sheets.map((data, idx) => (
          <StatSheetBody key={data.match.id || idx} data={data} />
        ))}
      </div>
      <style>{`
        .print-stat-sheet-page {
          min-height: 100vh;
          background: #f1f5f9;
          padding: 0;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .print-toolbar {
          position: sticky;
          top: 0;
          z-index: 20;
          background: #ffffff;
          border-bottom: 1px solid #e2e8f0;
          padding: 12px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .print-loader {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
        }
        .print-canvas {
          padding: 32px 16px 80px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 32px;
        }
        .stat-sheet {
          background: #ffffff;
          width: 100%;
          max-width: 850px;
          padding: 32px 36px;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
          color: #0f172a;
          break-inside: avoid;
        }
        .stat-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 24px;
          border-bottom: 2px solid #00247D;
          padding-bottom: 12px;
          margin-bottom: 20px;
        }
        .stat-brand {
          display: flex;
          gap: 12px;
          align-items: center;
          min-width: 0;
        }
        .stat-brand-logo {
          width: 52px;
          height: 52px;
          object-fit: contain;
        }
        .stat-org-name {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #64748b;
        }
        .stat-event-name {
          font-family: 'Barlow Condensed', 'Inter', sans-serif;
          font-size: 26px;
          font-weight: 700;
          color: #00247D;
          line-height: 1.15;
          margin: 0;
          text-transform: uppercase;
        }
        .stat-meta {
          display: grid;
          grid-template-columns: auto auto;
          gap: 4px 16px;
          font-size: 12px;
        }
        .stat-meta-label {
          font-weight: 700;
          letter-spacing: 0.12em;
          color: #64748b;
          margin-right: 8px;
        }
        .stat-meta-value {
          color: #0f172a;
          font-weight: 600;
        }
        .stat-scorebox {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          gap: 16px;
          align-items: center;
          padding: 12px 0 16px;
          border-bottom: 1px dashed #cbd5e1;
          margin-bottom: 16px;
        }
        .scorebox-team-name {
          font-weight: 700;
          font-size: 16px;
          color: #0f172a;
          margin-bottom: 6px;
        }
        .scorebox-final {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .scorebox-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.12em;
          color: #64748b;
        }
        .scorebox-blank {
          display: inline-block;
          width: 72px;
          height: 32px;
          border: 1px solid #94a3b8;
          border-radius: 4px;
          background: #ffffff;
        }
        .scorebox-vs {
          font-weight: 700;
          color: #94a3b8;
          font-size: 12px;
          letter-spacing: 0.16em;
        }
        .stat-sets {
          margin-bottom: 20px;
        }
        .stat-sets-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.14em;
          color: #64748b;
          margin-bottom: 6px;
        }
        .stat-sets-table {
          width: 100%;
          border-collapse: collapse;
        }
        .stat-sets-table th,
        .stat-sets-table td {
          border: 1px solid #94a3b8;
          padding: 6px 8px;
          font-size: 12px;
          text-align: center;
          height: 32px;
        }
        .stat-sets-table th {
          background: #f1f5f9;
          font-weight: 700;
          color: #475569;
          letter-spacing: 0.06em;
        }
        .stat-sets-table .team-cell {
          text-align: left;
          font-weight: 600;
          background: #f8fafc;
        }
        .stat-sets-table .blank {
          background: #ffffff;
        }
        .stat-teams {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .stat-team-block {
          break-inside: avoid;
        }
        .stat-team-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 6px 10px;
          background: #00247D;
          color: #ffffff;
          border-radius: 4px 4px 0 0;
        }
        .stat-team-logo {
          width: 32px;
          height: 32px;
          object-fit: contain;
          background: white;
          border-radius: 50%;
          padding: 2px;
        }
        .stat-team-side {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #cbd5e1;
        }
        .stat-team-name {
          font-family: 'Barlow Condensed', 'Inter', sans-serif;
          font-weight: 700;
          font-size: 18px;
          text-transform: uppercase;
        }
        /* Uniform column-width rules across every sport:
             Jersey  = 40px
             Name    = 30ch (fits ~30-char player names without ellipsis)
             Stats   = evenly divide the remaining sheet width
           The per-sport variant (.stats-cols-N) is set in JSX from
           stats.length -- 5 for netball/pickleball, 6 for soccer/volleyball,
           13 for basketball. */
        .stat-roster-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 4px;
          table-layout: fixed;
        }
        .stat-roster-table th,
        .stat-roster-table td {
          border: 1px solid #94a3b8;
          padding: 4px 6px;
          font-size: 12px;
          text-align: center;
          height: 26px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .stat-roster-table th {
          background: #f1f5f9;
          font-weight: 700;
          color: #475569;
          letter-spacing: 0.05em;
        }
        .col-jersey {
          width: 40px;
        }
        .col-name {
          text-align: left !important;
          width: 30ch;
          min-width: 30ch;
        }
        .stats-cols-5 .col-stat {
          width: calc((100% - 40px - 30ch) / 5);
        }
        .stats-cols-6 .col-stat {
          width: calc((100% - 40px - 30ch) / 6);
        }
        .stats-cols-7 .col-stat {
          width: calc((100% - 40px - 30ch) / 7);
        }
        .stats-cols-10 .col-stat {
          width: calc((100% - 40px - 30ch) / 10);
        }
        .stats-cols-13 .col-stat {
          width: calc((100% - 40px - 30ch) / 13);
        }
        .stat-roster-table tbody td.col-name {
          font-weight: 600;
        }
        .stat-roster-table tbody tr.walkon-row td {
          background: #fefce8;
        }
        .stat-roster-table tbody tr.totals-row td {
          background: #f1f5f9;
          font-weight: 700;
          color: #00247D;
          letter-spacing: 0.06em;
        }
        .totals-label {
          text-align: right !important;
          padding-right: 10px !important;
        }
        .stat-footer {
          margin-top: 24px;
          padding-top: 12px;
          border-top: 1px solid #cbd5e1;
          display: grid;
          grid-template-columns: 1fr 1fr auto;
          gap: 16px;
          align-items: center;
          font-size: 11px;
          color: #64748b;
        }
        .signature-line {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .signature-line > span:first-child {
          font-weight: 700;
          letter-spacing: 0.12em;
        }
        .signature-blank {
          height: 24px;
          border-bottom: 1px solid #94a3b8;
        }
        .stat-footer-brand {
          font-weight: 700;
          color: #00247D;
          letter-spacing: 0.14em;
        }
        .stat-sheet + .stat-sheet {
          page-break-before: always;
        }
        .stat-sheet.landscape {
          max-width: 1180px;
          padding: 22px 28px;
        }
        /* Basketball landscape: tighten the top-of-sheet chrome so each
           team's roster + walk-on rows + TOTALS row fit on one printed
           page. Values were tuned so a 3-player roster + 5 walk-ons + 1
           totals row (9 rows × 30px = 270px) per team fits on Letter
           landscape at 0.35in margins. */
        .stat-sheet.landscape .stat-header {
          padding-bottom: 6px;
          margin-bottom: 10px;
        }
        .stat-sheet.landscape .stat-event-name {
          font-size: 22px;
        }
        .stat-sheet.landscape .stat-scorebox {
          padding: 6px 0 8px;
          margin-bottom: 10px;
        }
        .stat-sheet.landscape .scorebox-team-name {
          font-size: 14px;
          margin-bottom: 4px;
        }
        .stat-sheet.landscape .scorebox-blank {
          height: 26px;
          width: 60px;
        }
        .stat-sheet.landscape .stat-teams {
          gap: 12px;
        }
        .stat-sheet.landscape .stat-team-header {
          padding: 4px 8px;
        }
        .stat-sheet.landscape .stat-team-name {
          font-size: 16px;
        }
        .stat-sheet.landscape .stat-team-logo {
          width: 26px;
          height: 26px;
        }
        /* 30px roster rows on basketball sheet so statisticians have real
           vertical room to write tally marks in each cell. */
        .stat-sheet.landscape .stat-roster-table th,
        .stat-sheet.landscape .stat-roster-table td {
          height: 30px;
          font-size: 12px;
          padding: 4px 6px;
        }
        .stat-sheet.landscape .stat-footer {
          margin-top: 12px;
          padding-top: 8px;
        }
        .stat-legend {
          display: flex;
          flex-wrap: wrap;
          align-items: baseline;
          gap: 2px 12px;
          margin-top: 10px;
          padding-top: 6px;
          border-top: 1px solid #e2e8f0;
          font-size: 9px;
          line-height: 1.35;
          color: #475569;
          break-inside: avoid;
        }
        .stat-legend-title {
          font-weight: 700;
          letter-spacing: 0.14em;
          color: #64748b;
        }
        .stat-legend-item b {
          color: #00247D;
          margin-right: 2px;
        }
        .stat-sheet.landscape .stat-legend {
          margin-top: 6px;
          padding-top: 4px;
          font-size: 8.5px;
        }

        @media print {
          body { background: #ffffff !important; }
          .no-print { display: none !important; }
          .print-stat-sheet-page { background: #ffffff !important; }
          .print-canvas {
            padding: 0 !important;
            gap: 0 !important;
          }
          .stat-sheet {
            box-shadow: none !important;
            padding: 18px 22px !important;
            max-width: 100% !important;
            page-break-after: always;
          }
          .stat-sheet:last-child {
            page-break-after: auto;
          }
          .stat-sheet.landscape {
            page: sheet-landscape;
          }
          @page {
            size: letter portrait;
            margin: 0.4in;
          }
          @page sheet-landscape {
            size: letter landscape;
            margin: 0.35in;
          }
        }
      `}</style>
    </div>
  );
}
